<template>
    <div class="page purchase-req-page">
        <div class="page-header">
            <h1>同意與契約設定</h1>
            <p class="page-header__desc">
                商品購買與課程報名共用：全系統預設、同意條款組與契約模板
            </p>
        </div>

        <div class="content vaporwave-theme purchase-req-content">
            <a-tabs v-model:activeKey="activeTab" class="req-tabs" type="card">
                <a-tab-pane key="system" tab="全系統設定">
                    <section class="req-block">
                        <p class="req-tab-hint">
                            控制是否啟用購買／報名流程，以及未特別指定商品時的預設模式。
                        </p>
                        <div class="req-block__body">
                            <div class="settings-rows">
                                <div class="setting-row">
                                    <span class="setting-row__label">
                                        啟用流程
                                    </span>
                                    <div class="setting-row__control">
                                        <a-switch
                                            v-model:checked="
                                                settings.enrollment_flow_enabled
                                            "
                                        />
                                        <span class="setting-row__hint">
                                            {{
                                                settings.enrollment_flow_enabled
                                                    ? '已啟用'
                                                    : '已關閉'
                                            }}
                                        </span>
                                    </div>
                                </div>
                                <div class="setting-row">
                                    <span class="setting-row__label">
                                        預設模式
                                    </span>
                                    <div class="setting-row__control">
                                        <a-radio-group
                                            v-model:value="
                                                settings.default_purchase_requirement_type
                                            "
                                            class="settings-radio-group"
                                            option-type="button"
                                            button-style="solid"
                                            size="small"
                                        >
                                            <a-radio-button value="none">
                                                無
                                            </a-radio-button>
                                            <a-radio-button value="consent">
                                                勾選同意
                                            </a-radio-button>
                                            <a-radio-button value="contract">
                                                簽署契約
                                            </a-radio-button>
                                        </a-radio-group>
                                    </div>
                                </div>
                            </div>
                            <div class="settings-actions">
                                <a-button
                                    type="primary"
                                    :loading="settingsSaving"
                                    @click="saveSettings"
                                >
                                    儲存設定
                                </a-button>
                            </div>
                        </div>
                    </section>
                </a-tab-pane>

                <a-tab-pane key="consent">
                    <template #tab>
                        <span>同意條款組</span>
                        <span v-if="profiles.length" class="req-tab-count">
                            {{ profiles.length }}
                        </span>
                    </template>
                    <section class="req-block">
                        <div class="req-block__head">
                            <p class="req-tab-hint req-tab-hint--inline">
                                供「勾選同意」模式使用：可綁到全站或單一商品。
                            </p>
                            <a-button
                                type="primary"
                                size="small"
                                class="unified-add-btn"
                                @click="openProfileModal()"
                            >
                                新增
                            </a-button>
                        </div>
                        <div class="req-block__body req-block__body--table">
                            <a-table
                                :columns="profileColumns"
                                :data-source="profiles"
                                :loading="profilesLoading"
                                row-key="id"
                                size="middle"
                                :pagination="false"
                            >
                                <template #bodyCell="{ column, record }">
                                    <template v-if="column.key === 'items'">
                                        {{ (record.items || []).length }} 項
                                    </template>
                                    <template
                                        v-else-if="
                                            column.key === 'content_status'
                                        "
                                    >
                                        <a-tag
                                            :color="
                                                countItemsWithContent(
                                                    record.items
                                                ) ===
                                                (record.items || []).length
                                                    ? 'green'
                                                    : 'orange'
                                            "
                                        >
                                            {{
                                                countItemsWithContent(
                                                    record.items
                                                )
                                            }}/{{ (record.items || []).length }}
                                            已填
                                        </a-tag>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'is_active'"
                                    >
                                        <a-tag
                                            :color="
                                                record.is_active
                                                    ? 'green'
                                                    : 'default'
                                            "
                                        >
                                            {{
                                                record.is_active
                                                    ? '啟用'
                                                    : '停用'
                                            }}
                                        </a-tag>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'action'"
                                    >
                                        <a-space>
                                            <a-button
                                                type="link"
                                                size="small"
                                                @click="
                                                    openProfileModal(record)
                                                "
                                            >
                                                編輯
                                            </a-button>
                                            <a-popconfirm
                                                title="確定刪除？"
                                                @confirm="
                                                    removeProfile(record.id)
                                                "
                                            >
                                                <a-button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                >
                                                    刪除
                                                </a-button>
                                            </a-popconfirm>
                                        </a-space>
                                    </template>
                                </template>
                            </a-table>
                        </div>
                    </section>
                </a-tab-pane>

                <a-tab-pane key="contract">
                    <template #tab>
                        <span>契約範本</span>
                        <span v-if="templates.length" class="req-tab-count">
                            {{ templates.length }}
                        </span>
                    </template>
                    <section class="req-block">
                        <div class="req-block__head">
                            <p class="req-tab-hint req-tab-hint--inline">
                                供「簽署契約」模式使用：定義契約內文、填寫欄位與簽署設定。
                            </p>
                            <a-button
                                type="primary"
                                size="small"
                                class="unified-add-btn"
                                @click="openTemplateModal()"
                            >
                                新增
                            </a-button>
                        </div>
                        <div class="req-block__body req-block__body--table">
                            <a-table
                                :columns="templateColumns"
                                :data-source="templates"
                                :loading="templatesLoading"
                                row-key="id"
                                size="middle"
                                :pagination="false"
                            >
                                <template #bodyCell="{ column, record }">
                                    <template v-if="column.key === 'flags'">
                                        <a-tag
                                            v-if="record.is_default"
                                            color="blue"
                                        >
                                            預設
                                        </a-tag>
                                        <a-tag
                                            :color="
                                                record.is_active
                                                    ? 'green'
                                                    : 'default'
                                            "
                                        >
                                            {{
                                                record.is_active
                                                    ? '啟用'
                                                    : '停用'
                                            }}
                                        </a-tag>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'action'"
                                    >
                                        <a-space>
                                            <a-button
                                                type="link"
                                                size="small"
                                                @click="
                                                    openTemplateModal(record)
                                                "
                                            >
                                                編輯
                                            </a-button>
                                            <a-popconfirm
                                                title="確定刪除？"
                                                @confirm="
                                                    removeTemplate(record.id)
                                                "
                                            >
                                                <a-button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                >
                                                    刪除
                                                </a-button>
                                            </a-popconfirm>
                                        </a-space>
                                    </template>
                                </template>
                            </a-table>
                        </div>
                    </section>
                </a-tab-pane>
            </a-tabs>
        </div>

        <a-modal
            v-model:open="profileModal.open"
            :title="profileModal.form.id ? '編輯同意條款組' : '新增同意條款組'"
            width="880px"
            :confirm-loading="profileModal.saving"
            :body-style="{
                maxHeight: '76vh',
                overflowY: 'auto',
                padding: '20px 24px 16px'
            }"
            ok-text="儲存"
            @ok="handleSaveProfile"
        >
            <a-form layout="vertical" class="modal-form">
                <a-row :gutter="20">
                    <a-col :span="16">
                        <a-form-item
                            label="名稱"
                            required
                            class="modal-form-item"
                        >
                            <a-input
                                v-model:value="profileModal.form.name"
                                placeholder="例如：全站通用同意條款"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="8">
                        <a-form-item label="啟用" class="modal-form-item">
                            <a-switch
                                v-model:checked="profileModal.form.is_active"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>

                <div class="items-editor">
                    <div class="items-editor__head">
                        <span class="items-editor__title">
                            勾選項目
                            <span class="items-editor__count">
                                {{ profileModal.form.items.length }} 項
                            </span>
                        </span>
                        <a-button
                            size="small"
                            class="inline-add-btn"
                            @click="addProfileItem"
                        >
                            新增項目
                        </a-button>
                    </div>

                    <a-empty
                        v-if="!profileModal.form.items.length"
                        description="尚未新增勾選項目"
                    />

                    <a-collapse
                        v-else
                        v-model:activeKey="profileModal.expandedKey"
                        accordion
                        :bordered="false"
                        expand-icon-position="end"
                        class="item-collapse"
                    >
                        <a-collapse-panel
                            v-for="(item, idx) in profileModal.form.items"
                            :key="item._uid"
                        >
                            <template #header>
                                <div class="collapse-header">
                                    <span class="collapse-header__idx">
                                        {{ idx + 1 }}
                                    </span>
                                    <span class="collapse-header__title">
                                        {{ summarizeItemLabel(item.label) }}
                                    </span>
                                    <a-tag
                                        v-if="item.required"
                                        class="collapse-header__tag"
                                    >
                                        必填
                                    </a-tag>
                                    <a-tag
                                        :color="
                                            item.content?.trim()
                                                ? 'green'
                                                : 'orange'
                                        "
                                        class="collapse-header__tag"
                                    >
                                        {{
                                            item.content?.trim()
                                                ? '已填條款'
                                                : '缺條款'
                                        }}
                                    </a-tag>
                                </div>
                            </template>
                            <template #extra>
                                <a-button
                                    type="text"
                                    danger
                                    size="small"
                                    @click.stop="removeProfileItem(idx)"
                                >
                                    刪除
                                </a-button>
                            </template>

                            <div class="collapse-body">
                                <a-form-item
                                    label="前台顯示文字"
                                    required
                                    class="modal-form-item"
                                >
                                    <a-input
                                        v-model:value="item.label"
                                        placeholder="我已閱讀並同意《服務條款》"
                                    />
                                </a-form-item>
                                <a-form-item
                                    label="條款全文"
                                    required
                                    class="modal-form-item"
                                >
                                    <a-textarea
                                        v-model:value="item.content"
                                        placeholder="請貼上完整條款內容"
                                        :rows="10"
                                        show-count
                                        :maxlength="50000"
                                    />
                                </a-form-item>
                                <a-checkbox v-model:checked="item.required">
                                    此項為必填
                                </a-checkbox>
                            </div>
                        </a-collapse-panel>
                    </a-collapse>
                </div>
            </a-form>
        </a-modal>

        <a-modal
            v-model:open="templateModal.open"
            :title="templateModal.form.id ? '編輯契約範本' : '新增契約範本'"
            width="960px"
            :confirm-loading="templateModal.saving"
            :body-style="{
                maxHeight: '76vh',
                overflowY: 'auto',
                paddingTop: '8px'
            }"
            ok-text="儲存"
            @ok="handleSaveTemplate"
        >
            <a-tabs v-model:activeKey="templateModal.activeTab">
                <a-tab-pane key="basic" tab="基本設定">
                    <a-form layout="vertical">
                        <a-form-item label="名稱" required>
                            <a-input
                                v-model:value="templateModal.form.name"
                                placeholder="例如：財務進化樹課程契約書"
                            />
                        </a-form-item>
                        <a-row :gutter="16">
                            <a-col :span="12">
                                <a-form-item label="版本">
                                    <a-input-number
                                        v-if="!templateModal.form.id"
                                        v-model:value="
                                            templateModal.form.version
                                        "
                                        :min="1"
                                        style="width: 100%"
                                    />
                                    <div v-else class="version-readonly">
                                        <span class="version-readonly__num">
                                            v{{ templateModal.form.version }}
                                        </span>
                                        <p class="version-readonly__hint">
                                            修改契約內容或簽署設定並儲存後，版本會自動
                                            +1
                                        </p>
                                    </div>
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item label="狀態">
                                    <a-space>
                                        <a-switch
                                            v-model:checked="
                                                templateModal.form.is_default
                                            "
                                            checked-children="預設"
                                            un-checked-children="非預設"
                                        />
                                        <a-switch
                                            v-model:checked="
                                                templateModal.form.is_active
                                            "
                                            checked-children="啟用"
                                            un-checked-children="停用"
                                        />
                                    </a-space>
                                </a-form-item>
                            </a-col>
                        </a-row>
                    </a-form>
                </a-tab-pane>

                <a-tab-pane key="signing" tab="簽署設定">
                    <section class="signing-section field-picker-section">
                        <div
                            class="items-editor__head items-editor__head--flat"
                        >
                            <span class="items-editor__title">
                                學員填寫欄位
                                <span class="items-editor__count">
                                    已選 {{ enabledDataFields.length }} 項
                                </span>
                            </span>
                        </div>

                        <p class="field-picker-hint">
                            勾選要收集的欄位，展開下方項目可調整顯示名稱與是否必填
                        </p>

                        <a-checkbox-group
                            v-model:value="enabledFieldKeys"
                            class="field-picker-grid"
                        >
                            <label
                                v-for="field in templateModal.signing
                                    .dataFields"
                                :key="field.key"
                                class="field-picker-option"
                                :class="{
                                    'field-picker-option--on': field.enabled
                                }"
                            >
                                <a-checkbox :value="field.key">
                                    {{ field.presetLabel }}
                                </a-checkbox>
                            </label>
                        </a-checkbox-group>

                        <a-empty
                            v-if="!enabledDataFields.length"
                            class="field-picker-empty"
                            description="請至少選擇一個欄位"
                        />

                        <a-collapse
                            v-else
                            v-model:activeKey="templateModal.fieldExpandedKey"
                            accordion
                            :bordered="false"
                            expand-icon-position="end"
                            class="item-collapse field-config-collapse"
                        >
                            <a-collapse-panel
                                v-for="field in enabledDataFields"
                                :key="field.key"
                            >
                                <template #header>
                                    <div class="collapse-header">
                                        <span class="collapse-header__title">
                                            {{
                                                field.label?.trim() ||
                                                field.presetLabel
                                            }}
                                        </span>
                                        <a-tag
                                            v-if="field.required"
                                            class="collapse-header__tag"
                                        >
                                            必填
                                        </a-tag>
                                        <a-tag
                                            v-else
                                            class="collapse-header__tag"
                                        >
                                            選填
                                        </a-tag>
                                    </div>
                                </template>
                                <div class="collapse-body">
                                    <a-form-item
                                        label="前台顯示名稱"
                                        class="modal-form-item"
                                    >
                                        <a-input
                                            v-model:value="field.label"
                                            :placeholder="field.presetLabel"
                                        />
                                    </a-form-item>
                                    <a-checkbox
                                        v-model:checked="field.required"
                                    >
                                        此欄位必填
                                    </a-checkbox>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>
                    </section>

                    <section class="signing-section">
                        <a-form-item
                            label="簽名區標籤"
                            class="signing-form-item"
                        >
                            <a-input
                                v-model:value="
                                    templateModal.signing.signatureLabel
                                "
                                placeholder="乙方（學員）簽名"
                            />
                        </a-form-item>
                    </section>

                    <section class="signing-section">
                        <div class="items-editor__head">
                            <span class="items-editor__title">
                                簽署前同意事項
                            </span>
                            <a-button
                                size="small"
                                class="inline-add-btn"
                                @click="addTemplateAgreement"
                            >
                                新增同意項
                            </a-button>
                        </div>
                        <a-empty
                            v-if="!templateModal.signing.agreements.length"
                            description="尚未新增同意事項"
                        />
                        <a-collapse
                            v-else
                            v-model:activeKey="
                                templateModal.agreementExpandedKey
                            "
                            accordion
                            :bordered="false"
                            expand-icon-position="end"
                            class="item-collapse"
                        >
                            <a-collapse-panel
                                v-for="(item, idx) in templateModal.signing
                                    .agreements"
                                :key="item._uid"
                            >
                                <template #header>
                                    <div class="collapse-header">
                                        <span class="collapse-header__idx">
                                            {{ idx + 1 }}
                                        </span>
                                        <span class="collapse-header__title">
                                            {{ summarizeItemLabel(item.label) }}
                                        </span>
                                        <a-tag
                                            v-if="item.required"
                                            class="collapse-header__tag"
                                        >
                                            必填
                                        </a-tag>
                                    </div>
                                </template>
                                <template #extra>
                                    <a-button
                                        type="text"
                                        danger
                                        size="small"
                                        @click.stop="
                                            removeTemplateAgreement(idx)
                                        "
                                    >
                                        刪除
                                    </a-button>
                                </template>
                                <div class="collapse-body">
                                    <a-form-item
                                        label="顯示文字"
                                        class="modal-form-item"
                                    >
                                        <a-textarea
                                            v-model:value="item.label"
                                            placeholder="例如：我已閱讀並同意本契約全部內容"
                                            :rows="4"
                                            show-count
                                            :maxlength="500"
                                        />
                                    </a-form-item>
                                    <a-checkbox v-model:checked="item.required">
                                        此項為必填
                                    </a-checkbox>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>
                    </section>
                </a-tab-pane>

                <a-tab-pane key="content" tab="契約內容">
                    <div class="editor-panel">
                        <div class="editor-panel__toolbar">
                            <div
                                class="view-mode-switch"
                                role="tablist"
                                aria-label="契約編輯模式"
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    class="view-mode-switch__btn"
                                    :class="{
                                        'view-mode-switch__btn--active':
                                            templateModal.contentMode ===
                                            'simple'
                                    }"
                                    :aria-selected="
                                        templateModal.contentMode === 'simple'
                                    "
                                    @click="switchContentMode('simple')"
                                >
                                    簡易條文
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    class="view-mode-switch__btn"
                                    :class="{
                                        'view-mode-switch__btn--active':
                                            templateModal.contentMode ===
                                            'advanced'
                                    }"
                                    :aria-selected="
                                        templateModal.contentMode === 'advanced'
                                    "
                                    @click="switchContentMode('advanced')"
                                >
                                    進階 HTML
                                </button>
                            </div>
                            <div
                                class="view-mode-switch"
                                role="tablist"
                                aria-label="契約內容檢視模式"
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    class="view-mode-switch__btn"
                                    :class="{
                                        'view-mode-switch__btn--active':
                                            templateModal.contentView === 'edit'
                                    }"
                                    :aria-selected="
                                        templateModal.contentView === 'edit'
                                    "
                                    @click="templateModal.contentView = 'edit'"
                                >
                                    編輯
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    class="view-mode-switch__btn"
                                    :class="{
                                        'view-mode-switch__btn--active':
                                            templateModal.contentView ===
                                            'preview'
                                    }"
                                    :aria-selected="
                                        templateModal.contentView === 'preview'
                                    "
                                    @click="openContractPreview"
                                >
                                    預覽
                                </button>
                            </div>
                            <div class="editor-panel__actions">
                                <a-tag
                                    :color="
                                        templateModal.contentMode === 'simple'
                                            ? 'blue'
                                            : 'purple'
                                    "
                                >
                                    {{
                                        templateModal.contentMode === 'simple'
                                            ? '系統組裝'
                                            : '自訂 HTML'
                                    }}
                                </a-tag>
                                <a-button
                                    v-if="
                                        templateModal.contentMode === 'simple'
                                    "
                                    size="small"
                                    class="toolbar-btn toolbar-btn--secondary"
                                    @click="loadSimpleDefaultDocument"
                                >
                                    載入簡易範本
                                </a-button>
                                <a-button
                                    v-else
                                    size="small"
                                    class="toolbar-btn toolbar-btn--secondary"
                                    @click="loadSystemDefaultBodyHtml"
                                >
                                    載入預設 HTML
                                </a-button>
                            </div>
                        </div>

                        <template v-if="templateModal.contentView === 'edit'">
                            <div
                                v-if="templateModal.contentMode === 'simple'"
                                class="simple-doc"
                            >
                                <p class="simple-doc__hint">
                                    用一般文字編修條文即可。簽名區與同意勾選會依「簽署設定」自動產生。
                                    可插入佔位符：例如
                                    <code>{姓名}</code>
                                    、
                                    <code>{課程名稱}</code>
                                    、
                                    <code>{課程費用}</code>
                                    。
                                </p>

                                <div class="token-bar">
                                    <span class="token-bar__label">
                                        插入欄位
                                    </span>
                                    <a-button
                                        v-for="token in documentTokens"
                                        :key="token.token"
                                        size="small"
                                        class="token-chip"
                                        @click="
                                            insertDocumentToken(token.token)
                                        "
                                    >
                                        {{ token.label }}
                                    </a-button>
                                </div>

                                <a-form
                                    layout="vertical"
                                    class="simple-doc__form"
                                >
                                    <a-form-item label="契約標題">
                                        <a-input
                                            v-model:value="
                                                templateModal.document.title
                                            "
                                            placeholder="例如：財務進化樹課程契約書"
                                        />
                                    </a-form-item>
                                    <a-form-item label="開頭（立約人／甲乙方）">
                                        <a-textarea
                                            v-model:value="
                                                templateModal.document
                                                    .parties_intro
                                            "
                                            :rows="3"
                                            placeholder="立契約書人&#10;甲方：…&#10;乙方：{姓名}"
                                        />
                                    </a-form-item>
                                    <a-form-item label="前言">
                                        <a-textarea
                                            v-model:value="
                                                templateModal.document.preamble
                                            "
                                            :rows="3"
                                            placeholder="茲因乙方報名參加…"
                                        />
                                    </a-form-item>

                                    <div
                                        class="items-editor__head items-editor__head--flat"
                                    >
                                        <span class="items-editor__title">
                                            條文
                                            <span class="items-editor__count">
                                                {{
                                                    templateModal.document
                                                        .articles.length
                                                }}
                                                條
                                            </span>
                                        </span>
                                        <a-button
                                            size="small"
                                            class="inline-add-btn"
                                            @click="addArticle"
                                        >
                                            新增條文
                                        </a-button>
                                    </div>

                                    <a-empty
                                        v-if="
                                            !templateModal.document.articles
                                                .length
                                        "
                                        description="尚未新增條文"
                                    />
                                    <a-collapse
                                        v-else
                                        v-model:activeKey="
                                            templateModal.articleExpandedKey
                                        "
                                        accordion
                                        :bordered="false"
                                        expand-icon-position="end"
                                        class="item-collapse"
                                    >
                                        <a-collapse-panel
                                            v-for="(
                                                article, idx
                                            ) in templateModal.document
                                                .articles"
                                            :key="article._uid"
                                        >
                                            <template #header>
                                                <div class="collapse-header">
                                                    <span
                                                        class="collapse-header__idx"
                                                    >
                                                        {{ idx + 1 }}
                                                    </span>
                                                    <span
                                                        class="collapse-header__title"
                                                    >
                                                        {{
                                                            article.title?.trim() ||
                                                            '未命名條文'
                                                        }}
                                                    </span>
                                                </div>
                                            </template>
                                            <template #extra>
                                                <a-button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    @click.stop="
                                                        removeArticle(idx)
                                                    "
                                                >
                                                    刪除
                                                </a-button>
                                            </template>
                                            <div class="collapse-body">
                                                <a-form-item
                                                    label="條文標題"
                                                    class="modal-form-item"
                                                >
                                                    <a-input
                                                        v-model:value="
                                                            article.title
                                                        "
                                                        placeholder="例如：第一條【課程內容與規格】"
                                                    />
                                                </a-form-item>
                                                <a-form-item
                                                    label="條文內容"
                                                    class="modal-form-item"
                                                >
                                                    <a-textarea
                                                        v-model:value="
                                                            article.body
                                                        "
                                                        :rows="6"
                                                        placeholder="一、…&#10;二、…（換行分段；一、二、會自動編號樣式）"
                                                    />
                                                </a-form-item>
                                            </div>
                                        </a-collapse-panel>
                                    </a-collapse>

                                    <a-form-item
                                        label="甲方資訊（每行一項）"
                                        class="simple-doc__party-a"
                                    >
                                        <a-textarea
                                            v-model:value="partyALinesText"
                                            :rows="4"
                                            placeholder="甲方：天諭科技有限公司&#10;代表人：…&#10;統一編號：…"
                                        />
                                    </a-form-item>
                                </a-form>
                            </div>

                            <a-textarea
                                v-else
                                v-model:value="templateModal.form.body_html"
                                class="code-textarea"
                                :rows="18"
                                placeholder="進階模式：直接編輯 HTML／Blade。一般情況請用「簡易條文」。"
                            />
                        </template>

                        <div v-else class="editor-panel__preview">
                            <a-spin :spinning="templateModal.previewLoading">
                                <iframe
                                    :srcdoc="livePreviewHtml"
                                    title="契約預覽"
                                    class="contract-preview-frame"
                                    sandbox=""
                                />
                            </a-spin>
                            <p
                                v-if="templateModal.previewError"
                                class="preview-error"
                            >
                                {{ templateModal.previewError }}
                            </p>
                        </div>
                    </div>

                    <a-collapse
                        v-model:activeKey="templateModal.advancedJsonOpen"
                        :bordered="false"
                        class="advanced-json-collapse"
                    >
                        <a-collapse-panel
                            key="json"
                            header="進階：簽署欄位 JSON（一般不需使用）"
                        >
                            <div class="editor-panel editor-panel--json">
                                <div
                                    class="editor-panel__toolbar editor-panel__toolbar--json"
                                >
                                    <div class="editor-panel__label-group">
                                        <span class="editor-panel__label">
                                            簽署欄位規格
                                        </span>
                                        <span class="editor-panel__label-hint">
                                            留空則沿用「簽署設定」；填寫後會覆寫簽署設定
                                        </span>
                                    </div>
                                    <div class="editor-panel__actions">
                                        <a-button
                                            size="small"
                                            class="toolbar-btn toolbar-btn--accent"
                                            @click="syncJsonFromSigning"
                                        >
                                            從簽署設定產生
                                        </a-button>
                                        <a-button
                                            v-if="
                                                templateModal.field_spec_text?.trim()
                                            "
                                            size="small"
                                            class="toolbar-btn toolbar-btn--ghost-danger"
                                            @click="
                                                templateModal.field_spec_text =
                                                    ''
                                            "
                                        >
                                            清除
                                        </a-button>
                                    </div>
                                </div>
                                <a-textarea
                                    v-model:value="
                                        templateModal.field_spec_text
                                    "
                                    class="code-textarea code-textarea--json"
                                    :rows="10"
                                    placeholder="留空即可（建議）"
                                />
                                <p
                                    v-if="fieldSpecPreviewError"
                                    class="preview-error"
                                >
                                    JSON 格式錯誤
                                </p>
                            </div>
                        </a-collapse-panel>
                    </a-collapse>
                </a-tab-pane>
            </a-tabs>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    buildConsentItemsPayload,
    slugifyConsentKey
} from '@/utils/slugifyConsentKey';
import { message } from 'ant-design-vue';
import {
    getShopSettings,
    updateShopSettings,
    getConsentProfiles,
    createConsentProfile,
    updateConsentProfile,
    deleteConsentProfile,
    getConsentProfile,
    getContractTemplates,
    createContractTemplate,
    updateContractTemplate,
    deleteContractTemplate,
    getContractTemplateDefaults,
    previewContractTemplate,
    compileContractTemplate
} from '@/api/products/purchaseRequirement';
import { fetchAllOptionRows } from '@/utils/safePaginatedFetch';

const route = useRoute();
const router = useRouter();

const TAB_KEYS = ['system', 'consent', 'contract'];
const resolveTab = (key) => (TAB_KEYS.includes(key) ? key : 'system');
const activeTab = ref(resolveTab(String(route.query.tab || 'system')));

watch(activeTab, (key) => {
    if (String(route.query.tab || 'system') === key) return;
    router.replace({ query: { ...route.query, tab: key } });
});

watch(
    () => route.query.tab,
    (tab) => {
        const next = resolveTab(String(tab || 'system'));
        if (activeTab.value !== next) activeTab.value = next;
    }
);

const FIELD_PRESETS = [
    {
        key: 'name',
        presetLabel: '姓名',
        label: '姓名',
        type: 'text',
        max_length: 50,
        required: true
    },
    {
        key: 'id_number',
        presetLabel: '身分證字號',
        label: '身分證字號',
        type: 'text',
        max_length: 20,
        required: true
    },
    {
        key: 'phone',
        presetLabel: '聯絡電話',
        label: '聯絡電話',
        type: 'text',
        max_length: 20,
        required: true
    },
    {
        key: 'email',
        presetLabel: '電子信箱',
        label: '電子信箱',
        type: 'email',
        max_length: 255,
        required: false
    },
    {
        key: 'address',
        presetLabel: '地址',
        label: '地址',
        type: 'text',
        max_length: 255,
        required: false
    },
    {
        key: 'company_name',
        presetLabel: '公司／名稱',
        label: '公司／名稱',
        type: 'text',
        max_length: 100,
        required: true
    },
    {
        key: 'title',
        presetLabel: '職稱',
        label: '職稱',
        type: 'text',
        max_length: 50,
        required: false
    },
    {
        key: 'contact_person',
        presetLabel: '聯絡人',
        label: '聯絡人',
        type: 'text',
        max_length: 50,
        required: false
    }
];

let agreementUid = 0;
const nextAgreementUid = () => {
    agreementUid += 1;
    return `agr-${agreementUid}`;
};

let profileItemUid = 0;
const nextProfileItemUid = () => {
    profileItemUid += 1;
    return `cpi-${profileItemUid}`;
};

const slugifyAgreementKey = (label, index, usedKeys) =>
    slugifyConsentKey(label, index, usedKeys, 'agreement');

const settings = reactive({
    enrollment_flow_enabled: true,
    default_purchase_requirement_type: 'none'
});
const settingsSaving = ref(false);

const profiles = ref([]);
const profilesLoading = ref(false);
const templates = ref([]);
const templatesLoading = ref(false);

const profileColumns = [
    { title: '名稱', dataIndex: 'name', key: 'name' },
    { title: '項目數', key: 'items', width: 90 },
    { title: '條款', key: 'content_status', width: 120 },
    { title: '狀態', key: 'is_active', width: 90 },
    { title: '操作', key: 'action', width: 140 }
];

const templateColumns = [
    { title: '名稱', dataIndex: 'name', key: 'name' },
    { title: '版本', dataIndex: 'version', key: 'version', width: 80 },
    { title: '標記', key: 'flags', width: 140 },
    { title: '操作', key: 'action', width: 140 }
];

const profileModal = reactive({
    open: false,
    saving: false,
    expandedKey: undefined,
    form: { id: null, name: '', is_active: true, items: [] }
});

const templateModal = reactive({
    open: false,
    saving: false,
    activeTab: 'signing',
    agreementExpandedKey: undefined,
    fieldExpandedKey: undefined,
    articleExpandedKey: undefined,
    advancedJsonOpen: [],
    form: {
        id: null,
        name: '',
        version: 1,
        is_default: false,
        is_active: true,
        body_html: ''
    },
    signing: {
        dataFields: [],
        agreements: [],
        signatureLabel: '乙方（學員）簽名'
    },
    document: {
        title: '',
        parties_intro: '',
        preamble: '',
        articles: [],
        party_a_lines: []
    },
    contentMode: 'simple',
    field_spec_text: '',
    contentView: 'edit',
    previewLoading: false,
    previewError: '',
    previewHtml: ''
});

let articleUid = 0;
const nextArticleUid = () => {
    articleUid += 1;
    return `art-${articleUid}`;
};

const documentTokens = ref([
    { token: '{姓名}', label: '學員姓名', key: 'name' },
    { token: '{身分證字號}', label: '身分證', key: 'id_number' },
    { token: '{聯絡電話}', label: '電話', key: 'phone' },
    { token: '{電子信箱}', label: 'Email', key: 'email' },
    { token: '{地址}', label: '地址', key: 'address' },
    { token: '{課程名稱}', label: '課程名稱', key: 'product_name' },
    { token: '{課程費用}', label: '課程費用', key: 'amount_formatted' }
]);

const emptyDocument = () => ({
    title: '',
    parties_intro: '',
    preamble: '',
    articles: [],
    party_a_lines: []
});

const normalizeDocumentArticles = (articles = []) =>
    (articles || []).map((article) => ({
        _uid: nextArticleUid(),
        title: article?.title || '',
        body: article?.body || ''
    }));

const applyDocumentSource = (source = {}) => {
    templateModal.document.title = source.title || '';
    templateModal.document.parties_intro = source.parties_intro || '';
    templateModal.document.preamble = source.preamble || '';
    templateModal.document.articles = normalizeDocumentArticles(
        source.articles || []
    );
    templateModal.document.party_a_lines = Array.isArray(source.party_a_lines)
        ? [...source.party_a_lines]
        : [];
};

const buildDocumentSourcePayload = () => ({
    mode: templateModal.contentMode === 'advanced' ? 'advanced' : 'simple',
    title: templateModal.document.title || '',
    parties_intro: templateModal.document.parties_intro || '',
    preamble: templateModal.document.preamble || '',
    articles: (templateModal.document.articles || []).map(
        ({ title, body }) => ({
            title: title || '',
            body: body || ''
        })
    ),
    party_a_lines: Array.isArray(templateModal.document.party_a_lines)
        ? templateModal.document.party_a_lines.filter((line) =>
              String(line || '').trim()
          )
        : []
});

const partyALinesText = computed({
    get() {
        return (templateModal.document.party_a_lines || []).join('\n');
    },
    set(value) {
        templateModal.document.party_a_lines = String(value || '')
            .split(/\r?\n/)
            .map((line) => line.trimEnd());
    }
});

let systemDefaultsCache = null;

const enabledDataFields = computed(() =>
    templateModal.signing.dataFields.filter((field) => field.enabled)
);

const enabledFieldKeys = computed({
    get() {
        return templateModal.signing.dataFields
            .filter((field) => field.enabled)
            .map((field) => field.key);
    },
    set(keys) {
        const keySet = new Set(keys);
        let newlyEnabledKey = null;

        templateModal.signing.dataFields.forEach((field) => {
            const wasEnabled = field.enabled;
            field.enabled = keySet.has(field.key);
            if (field.enabled && !wasEnabled) {
                newlyEnabledKey = field.key;
                if (!field.label?.trim()) {
                    field.label = field.presetLabel;
                }
            }
        });

        if (newlyEnabledKey) {
            templateModal.fieldExpandedKey = newlyEnabledKey;
        } else if (
            templateModal.fieldExpandedKey &&
            !keySet.has(templateModal.fieldExpandedKey)
        ) {
            templateModal.fieldExpandedKey = keys[0] ?? undefined;
        }
    }
});

// 簽署設定變更後清除進階 JSON，避免儲存時仍送出舊的 field_spec 覆寫
watch(
    () => templateModal.signing,
    () => {
        if (templateModal.open && templateModal.field_spec_text?.trim()) {
            templateModal.field_spec_text = '';
        }
    },
    { deep: true }
);

const livePreviewHtml = computed(() => {
    if (templateModal.previewHtml?.trim()) {
        return templateModal.previewHtml;
    }
    return `<!DOCTYPE html><html><body style="font-family:'Noto Sans TC',sans-serif;padding:32px;color:#999;text-align:center;"><p>點「預覽」載入真實契約畫面</p></body></html>`;
});

const fieldSpecPreviewError = computed(() => {
    const textValue = templateModal.field_spec_text?.trim();
    if (!textValue) {
        return false;
    }
    try {
        JSON.parse(textValue);
        return false;
    } catch {
        return true;
    }
});

const addArticle = () => {
    const uid = nextArticleUid();
    templateModal.document.articles.push({
        _uid: uid,
        title: '',
        body: ''
    });
    templateModal.articleExpandedKey = uid;
};

const removeArticle = (idx) => {
    const item = templateModal.document.articles[idx];
    if (item && templateModal.articleExpandedKey === item._uid) {
        templateModal.articleExpandedKey = undefined;
    }
    templateModal.document.articles.splice(idx, 1);
};

const insertDocumentToken = (token) => {
    const target = templateModal.document;
    // 優先插入到前言；若前言已有內容且標題空則插標題旁提示
    if (!target.preamble?.includes(token)) {
        target.preamble = `${target.preamble || ''}${token}`;
        message.success(`已插入 ${token} 到前言（可自行剪貼到其他欄位）`);
        return;
    }
    target.parties_intro = `${target.parties_intro || ''}${token}`;
    message.success(`已插入 ${token}`);
};

const switchContentMode = async (mode) => {
    if (mode === templateModal.contentMode) {
        return;
    }
    if (mode === 'advanced') {
        const fieldSpec = resolveFieldSpecForSave();
        if (!fieldSpec) {
            return;
        }
        templateModal.previewLoading = true;
        try {
            const res = await compileContractTemplate({
                document_source: buildDocumentSourcePayload(),
                field_spec: fieldSpec
            });
            if (res?.result && res.data?.body_html) {
                templateModal.form.body_html = res.data.body_html;
            }
        } catch (e) {
            message.warning('無法從簡易條文編譯 HTML，請手動調整');
        } finally {
            templateModal.previewLoading = false;
        }
        templateModal.contentMode = 'advanced';
        templateModal.contentView = 'edit';
        message.info('已切換進階 HTML（已帶入簡易條文編譯結果）');
        return;
    }

    if (
        !(
            templateModal.document.title ||
            templateModal.document.articles.length
        )
    ) {
        await loadSimpleDefaultDocument();
    }
    templateModal.contentMode = 'simple';
    templateModal.contentView = 'edit';
};

const resolveFieldSpecForSave = () => {
    let fieldSpec = buildFieldSpecFromSigning(templateModal.signing);
    if (templateModal.field_spec_text?.trim()) {
        try {
            fieldSpec = JSON.parse(templateModal.field_spec_text);
        } catch {
            message.error('簽署欄位規格 JSON 格式錯誤');
            return null;
        }
    }
    return fieldSpec;
};

const openContractPreview = async () => {
    templateModal.contentView = 'preview';
    templateModal.previewLoading = true;
    templateModal.previewError = '';
    try {
        const fieldSpec = resolveFieldSpecForSave();
        if (!fieldSpec) {
            templateModal.previewError = '簽署設定無效';
            return;
        }
        const payload =
            templateModal.contentMode === 'simple'
                ? {
                      document_source: buildDocumentSourcePayload(),
                      field_spec: fieldSpec
                  }
                : {
                      body_html: templateModal.form.body_html,
                      field_spec: fieldSpec
                  };
        const res = await previewContractTemplate(payload);
        if (res?.result && res.data?.html) {
            templateModal.previewHtml = res.data.html;
        } else {
            templateModal.previewError = res?.message || '預覽失敗';
            templateModal.previewHtml = '';
        }
    } catch (e) {
        templateModal.previewError = e?.message || '預覽失敗';
        templateModal.previewHtml = '';
    } finally {
        templateModal.previewLoading = false;
    }
};

const loadSimpleDefaultDocument = async () => {
    const defaults = await fetchSystemDefaults();
    if (defaults?.document_source) {
        applyDocumentSource(defaults.document_source);
        if (defaults.tokens?.length) {
            documentTokens.value = defaults.tokens;
        }
        templateModal.contentMode = 'simple';
        templateModal.contentView = 'edit';
        message.success('已載入簡易條文範本');
        return;
    }
    message.error('無法載入簡易範本');
};

const syncJsonFromSigning = () => {
    templateModal.field_spec_text = JSON.stringify(
        buildFieldSpecFromSigning(templateModal.signing),
        null,
        2
    );
    templateModal.advancedJsonOpen = ['json'];
    message.success('已從簽署設定產生 JSON');
};

const loadSettings = async () => {
    const res = await getShopSettings();
    if (res?.result && res.data) {
        settings.enrollment_flow_enabled = !!res.data.enrollment_flow_enabled;
        settings.default_purchase_requirement_type =
            res.data.default_purchase_requirement_type || 'none';
    }
};

const saveSettings = async () => {
    settingsSaving.value = true;
    try {
        const res = await updateShopSettings({ ...settings });
        if (res?.result) message.success('設定已儲存');
        else message.error(res?.message || '儲存失敗');
    } finally {
        settingsSaving.value = false;
    }
};

const loadProfiles = async () => {
    profilesLoading.value = true;
    try {
        profiles.value = await fetchAllOptionRows((params) =>
            getConsentProfiles(params)
        );
    } finally {
        profilesLoading.value = false;
    }
};

const loadTemplates = async () => {
    templatesLoading.value = true;
    try {
        templates.value = await fetchAllOptionRows((params) =>
            getContractTemplates(params)
        );
    } finally {
        templatesLoading.value = false;
    }
};

const countItemsWithContent = (items = []) =>
    items.filter((item) => String(item?.content || '').trim()).length;

const summarizeItemLabel = (label) => {
    const text = String(label || '').trim();
    if (!text) return '未命名項目';
    return text.length > 36 ? `${text.slice(0, 36)}…` : text;
};

const normalizeProfileItems = (items = []) =>
    items.map((item) => ({
        _uid: nextProfileItemUid(),
        key: item?.key || '',
        label: item?.label || '',
        content: item?.content || '',
        required: item?.required !== false
    }));

const openProfileModal = async (record = null) => {
    profileModal.expandedKey = undefined;
    if (record?.id) {
        profileModal.open = true;
        profileModal.saving = false;
        profileModal.form = {
            id: record.id,
            name: record.name,
            is_active: !!record.is_active,
            items: []
        };
        const res = await getConsentProfile(record.id);
        if (res?.result && res.data) {
            profileModal.form = {
                id: res.data.id,
                name: res.data.name,
                is_active: !!res.data.is_active,
                items: normalizeProfileItems(res.data.items)
            };
        } else {
            profileModal.form.items = normalizeProfileItems(record.items);
            message.warning('無法載入完整資料，已使用列表快取');
        }
        return;
    }
    profileModal.form = { id: null, name: '', is_active: true, items: [] };
    profileModal.saving = false;
    profileModal.open = true;
};

const addProfileItem = () => {
    const uid = nextProfileItemUid();
    profileModal.form.items.push({
        _uid: uid,
        key: '',
        label: '',
        content: '',
        required: true
    });
    profileModal.expandedKey = uid;
};

const removeProfileItem = (idx) => {
    const item = profileModal.form.items[idx];
    if (item && profileModal.expandedKey === item._uid) {
        profileModal.expandedKey = undefined;
    }
    profileModal.form.items.splice(idx, 1);
};

const saveProfile = async () => {
    const name = profileModal.form.name?.trim();
    if (!name) {
        message.error('請填寫名稱');
        return false;
    }

    const items = buildConsentItemsPayload(profileModal.form.items);

    if (!items.length) {
        message.error('至少需一個勾選項目，並填寫前台顯示文字');
        return false;
    }

    const missingContent = items.filter((i) => !i.content);
    if (missingContent.length) {
        message.warning('部分項目尚未填寫條款全文，前台將無法展開預覽');
    }

    const payload = {
        name,
        is_active: profileModal.form.is_active,
        items
    };

    profileModal.saving = true;
    try {
        const res = profileModal.form.id
            ? await updateConsentProfile(profileModal.form.id, payload)
            : await createConsentProfile(payload);
        if (res?.result) {
            message.success('已儲存');
            profileModal.open = false;
            loadProfiles();
            return true;
        }
        message.error(res?.message || '儲存失敗');
        return false;
    } finally {
        profileModal.saving = false;
    }
};

const handleSaveProfile = async () => {
    const ok = await saveProfile();
    if (!ok) {
        return Promise.reject();
    }
};

const removeProfile = async (id) => {
    const res = await deleteConsentProfile(id);
    if (res?.result) {
        message.success('已刪除');
        loadProfiles();
    } else {
        message.error(res?.message || '刪除失敗');
    }
};

const fetchSystemDefaults = async () => {
    if (systemDefaultsCache) return systemDefaultsCache;
    const res = await getContractTemplateDefaults();
    if (res?.result && res.data) {
        systemDefaultsCache = res.data;
        return res.data;
    }
    return null;
};

const parseFieldSpecToSigning = (fieldSpec = {}) => {
    const fieldsObj = fieldSpec.contract_data_fields || {};
    const dataFields = FIELD_PRESETS.map((preset) => {
        const existing = fieldsObj[preset.key];
        return {
            ...preset,
            enabled: !!existing,
            label: existing?.label || preset.label,
            required: existing?.required ?? preset.required,
            type: existing?.type || preset.type,
            max_length: existing?.max_length || preset.max_length
        };
    });

    const agreementsObj = fieldSpec.agreements || {};
    const agreements = Object.entries(agreementsObj).map(([key, def]) => ({
        _uid: nextAgreementUid(),
        key,
        label: def?.label || key,
        required: def?.required !== false
    }));

    const signatureLabel =
        fieldSpec.signature_areas?.party_a?.label || '乙方（學員）簽名';

    return { dataFields, agreements, signatureLabel };
};

const buildFieldSpecFromSigning = (signing) => {
    const contract_data_fields = {};
    (signing.dataFields || [])
        .filter((f) => f.enabled && f.key)
        .forEach((f) => {
            contract_data_fields[f.key] = {
                label: f.label?.trim() || f.key,
                required: !!f.required,
                type: f.type || 'text',
                max_length: f.max_length || 255
            };
        });

    const agreements = {};
    const usedKeys = new Set();
    (signing.agreements || [])
        .filter((a) => a.label?.trim())
        .forEach((a, index) => {
            let key = a.key?.trim();
            if (!key || usedKeys.has(key)) {
                key = slugifyAgreementKey(a.label, index, usedKeys);
            }
            usedKeys.add(key);
            agreements[key] = {
                label: a.label.trim(),
                required: a.required !== false,
                type: 'checkbox'
            };
        });

    return {
        contract_data_fields,
        inline_fields: {},
        agreements,
        signature_areas: {
            party_a: {
                label: signing.signatureLabel?.trim() || '乙方（學員）簽名',
                type: 'signature',
                required: true
            }
        },
        rules_summary: {
            contract_data_required_hint:
                '標示 * 為必填，未填齊無法預覽契約與簽署。',
            signature_required_hint: '須完成簽名並勾選同意條款後方可送出。',
            agreements_hint: '簽署前必須勾選所有同意事項。'
        }
    };
};

const resetTemplateSigning = (fieldSpec = null) => {
    const parsed = parseFieldSpecToSigning(fieldSpec || {});
    templateModal.signing.dataFields = parsed.dataFields;
    templateModal.signing.agreements = parsed.agreements;
    templateModal.signing.signatureLabel = parsed.signatureLabel;
};

const addTemplateAgreement = () => {
    const uid = nextAgreementUid();
    templateModal.signing.agreements.push({
        _uid: uid,
        key: '',
        label: '',
        required: true
    });
    templateModal.agreementExpandedKey = uid;
};

const removeTemplateAgreement = (idx) => {
    const item = templateModal.signing.agreements[idx];
    if (item && templateModal.agreementExpandedKey === item._uid) {
        templateModal.agreementExpandedKey = undefined;
    }
    templateModal.signing.agreements.splice(idx, 1);
};

const loadSystemDefaultBodyHtml = async () => {
    const defaults = await fetchSystemDefaults();
    if (defaults?.body_html) {
        templateModal.form.body_html = defaults.body_html;
        templateModal.contentMode = 'advanced';
        templateModal.contentView = 'edit';
        message.success('已載入系統預設契約 HTML');
    } else {
        message.error('無法載入預設契約 HTML');
    }
};

const openTemplateModal = async (record = null) => {
    templateModal.activeTab = record ? 'signing' : 'basic';
    templateModal.field_spec_text = '';
    templateModal.saving = false;
    templateModal.agreementExpandedKey = undefined;
    templateModal.fieldExpandedKey = undefined;
    templateModal.articleExpandedKey = undefined;
    templateModal.advancedJsonOpen = [];
    templateModal.contentView = 'edit';
    templateModal.previewHtml = '';
    templateModal.previewError = '';

    if (record) {
        templateModal.form = {
            id: record.id,
            name: record.name,
            version: record.version || 1,
            is_default: !!record.is_default,
            is_active: record.is_active !== false,
            body_html: record.body_html || ''
        };
        resetTemplateSigning(record.field_spec || {});
        const source = record.field_spec?.document_source;
        if (
            source &&
            (source.mode === 'simple' ||
                source.articles?.length ||
                source.title)
        ) {
            applyDocumentSource(source);
            templateModal.contentMode =
                source.mode === 'advanced' ? 'advanced' : 'simple';
        } else {
            applyDocumentSource(emptyDocument());
            templateModal.contentMode = 'advanced';
        }
    } else {
        const defaults = await fetchSystemDefaults();
        templateModal.form = {
            id: null,
            name: '',
            version: 1,
            is_default: false,
            is_active: true,
            body_html: ''
        };
        resetTemplateSigning(defaults?.field_spec || {});
        applyDocumentSource(defaults?.document_source || emptyDocument());
        if (defaults?.tokens?.length) {
            documentTokens.value = defaults.tokens;
        }
        templateModal.contentMode = 'simple';
    }
    templateModal.open = true;
};

const saveTemplate = async () => {
    const name = templateModal.form.name?.trim();
    if (!name) {
        message.error('請填寫範本名稱');
        return false;
    }

    const fieldSpec = resolveFieldSpecForSave();
    if (!fieldSpec) {
        return false;
    }

    const enabledFields = Object.keys(fieldSpec.contract_data_fields || {});
    if (!enabledFields.length) {
        message.error('請至少啟用一個學員填寫欄位');
        return false;
    }

    const agreementKeys = Object.keys(fieldSpec.agreements || {});
    if (!agreementKeys.length) {
        message.error('請至少新增一項同意事項，並填寫顯示文字');
        return false;
    }

    if (templateModal.contentMode === 'simple') {
        if (!(templateModal.document.title || '').trim()) {
            message.error('請填寫契約標題');
            return false;
        }
        if (!(templateModal.document.articles || []).length) {
            message.error('請至少新增一條條文');
            return false;
        }
    } else if (!(templateModal.form.body_html || '').trim()) {
        message.error('進階模式請填寫契約 HTML，或切回簡易條文');
        return false;
    }

    const documentSource = buildDocumentSourcePayload();
    const payload = {
        name,
        version: templateModal.form.version,
        is_default: templateModal.form.is_default,
        is_active: templateModal.form.is_active,
        body_html: templateModal.form.body_html || '',
        field_spec: fieldSpec,
        document_source: documentSource
    };

    templateModal.saving = true;
    try {
        const res = templateModal.form.id
            ? await updateContractTemplate(templateModal.form.id, payload)
            : await createContractTemplate(payload);
        if (res?.result) {
            const savedVersion = res.data?.version;
            if (savedVersion != null && templateModal.form.id) {
                templateModal.form.version = savedVersion;
            }
            if (res.data?.body_html) {
                templateModal.form.body_html = res.data.body_html;
            }
            message.success(
                savedVersion != null && templateModal.form.id
                    ? `已儲存（版本 v${savedVersion}）`
                    : '已儲存'
            );
            templateModal.open = false;
            loadTemplates();
            return true;
        }
        message.error(res?.message || '儲存失敗');
        return false;
    } finally {
        templateModal.saving = false;
    }
};

const handleSaveTemplate = async () => {
    const ok = await saveTemplate();
    if (!ok) return Promise.reject();
};

const removeTemplate = async (id) => {
    const res = await deleteContractTemplate(id);
    if (res?.result) {
        message.success('已刪除');
        loadTemplates();
    } else {
        message.error(res?.message || '刪除失敗');
    }
};

onMounted(async () => {
    await Promise.all([loadSettings(), loadProfiles(), loadTemplates()]);
});
</script>

<style scoped>
.purchase-req-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: calc(100vh - 9rem);
}

.purchase-req-content {
    padding: 28px 32px !important;
}

/* ── 三種設置分頁 ── */
.req-tabs :deep(.ant-tabs-nav) {
    margin-bottom: 20px;
}

.req-tabs :deep(.ant-tabs-tab) {
    font-weight: 600;
    padding: 10px 18px !important;
}

.req-tabs :deep(.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active) {
    border-bottom-color: #fff;
}

.req-tabs :deep(.ant-tabs-content) {
    min-height: 280px;
}

.req-tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    color: #5b4a9c;
    background: rgba(42, 74, 122, 0.15);
    border: 1px solid rgba(42, 74, 122, 0.28);
}

.req-tab-hint {
    margin: 0 0 18px;
    font-size: 13px;
    line-height: 1.55;
    color: #6b6b7b;
}

.req-tab-hint--inline {
    margin: 0;
    flex: 1;
    min-width: 0;
}

.req-block__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}

.req-block__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.req-divider {
    margin: 28px 0;
    border-color: rgba(42, 74, 122, 0.15);
}

/* ── 設定列 ── */
.settings-rows {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.setting-row {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    gap: 24px;
    align-items: center;
}

.setting-row__label {
    font-size: 14px;
    font-weight: 600;
    color: #3a3a4a;
}

.setting-row__control {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-self: start;
    width: auto;
    max-width: 100%;
}

.setting-row__control :deep(.ant-switch) {
    flex: none;
    width: 44px;
    min-width: 44px;
}

.setting-row__hint {
    font-size: 13px;
    color: #8c8c8c;
}

.settings-radio-group {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
}

.settings-radio-group :deep(.ant-radio-button-wrapper) {
    min-width: 88px;
    height: 32px;
    line-height: 30px;
    text-align: center;
    border-radius: 6px !important;
    border-inline-start-width: 1px;
}

.settings-radio-group
    :deep(.ant-radio-button-wrapper:not(:first-child)::before) {
    display: none;
}

.settings-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid rgba(200, 200, 210, 0.35);
}

/* ── 表格 ── */
.req-block__body--table :deep(.ant-table) {
    font-size: 14px;
}

.req-block__body--table :deep(.ant-table-thead > tr > th) {
    padding: 12px 16px;
    background: rgba(248, 249, 250, 0.8);
    font-weight: 600;
    color: #4a4a5a;
}

.req-block__body--table :deep(.ant-table-tbody > tr > td) {
    padding: 14px 16px;
}

.field-hint {
    margin: 10px 0 0;
    color: #999;
    font-size: 12px;
    line-height: 1.5;
}

.items-editor__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    padding-top: 8px;
    border-top: 1px solid #eceef3;
}

.items-editor__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    font-size: 14px;
    color: #2a2a3a;
}

.items-editor__count {
    font-size: 12px;
    font-weight: 500;
    color: #8c8c8c;
    background: #f0f2f5;
    padding: 2px 8px;
    border-radius: 10px;
}

.modal-form-item {
    margin-bottom: 20px;
}

.modal-form-item :deep(.ant-form-item-label) {
    padding-bottom: 6px;
}

.modal-form-item :deep(.ant-form-item-label > label) {
    font-weight: 600;
    color: #3a3a4a;
}

/* ── 收合項目 ── */
.item-collapse {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: transparent;
}

.item-collapse :deep(.ant-collapse-item) {
    border: 1px solid #e4e6ec !important;
    border-radius: 12px !important;
    overflow: hidden;
    background: #fafbfc;
}

.item-collapse :deep(.ant-collapse-item-active) {
    background: #fff;
    border-color: #d3adf7 !important;
    box-shadow: 0 2px 8px rgba(114, 46, 209, 0.08);
}

.item-collapse :deep(.ant-collapse-header) {
    align-items: center !important;
    padding: 14px 16px !important;
}

.item-collapse :deep(.ant-collapse-content-box) {
    padding: 4px 20px 20px !important;
}

.collapse-header {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
    padding-right: 8px;
}

.collapse-header__idx {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: #722ed1;
    background: #f0e6ff;
    border-radius: 6px;
}

.collapse-header__title {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    color: #2a2a3a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.collapse-header__tag {
    flex-shrink: 0;
    margin: 0;
    font-size: 11px;
    line-height: 18px;
}

.collapse-body {
    padding-top: 4px;
}

.collapse-body .modal-form-item:last-of-type {
    margin-bottom: 12px;
}

.items-editor__field {
    margin-bottom: 0;
}

.items-editor__field--inline :deep(.ant-form-item-control-input) {
    min-height: 32px;
    display: flex;
    align-items: center;
}

.items-editor__card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    background: #fafafa;
}

.items-editor__card-title {
    font-weight: 700;
    color: #722ed1;
    margin-bottom: 8px;
}

.items-editor__card-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
}

.items-editor__card--compact {
    padding: 12px 16px;
    margin-bottom: 10px;
}

/* ── 契約 HTML / JSON 編輯器 ── */
.editor-panel {
    margin-bottom: 24px;
}

.editor-panel--json {
    padding-top: 20px;
    border-top: 1px solid #eceef3;
}

.editor-panel__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.editor-panel__toolbar--json {
    gap: 16px;
}

.editor-panel__toolbar--json .editor-panel__label-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-right: auto;
}

.editor-panel__label-hint {
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
    line-height: 1.4;
}

/* 編輯 / 預覽 segmented control */
.view-mode-switch {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background: #eef0f5;
    border: 1px solid #e2e5ec;
    border-radius: 10px;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
}

.view-mode-switch__btn {
    appearance: none;
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    padding: 7px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition:
        color 0.18s ease,
        background 0.18s ease,
        box-shadow 0.18s ease;
}

.view-mode-switch__btn:hover:not(.view-mode-switch__btn--active) {
    color: #4b5563;
    background: rgba(255, 255, 255, 0.55);
}

.view-mode-switch__btn--active {
    color: #1e3a5f;
    background: #ffffff;
    box-shadow:
        0 1px 3px rgba(15, 23, 42, 0.1),
        0 0 0 1px rgba(42, 74, 122, 0.1);
}

.view-mode-switch__btn:focus-visible {
    outline: 2px solid rgba(42, 74, 122, 0.35);
    outline-offset: 2px;
}

/* 工具列次要 / 強調按鈕 */
.toolbar-btn.ant-btn {
    height: 30px;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    line-height: 28px;
}

.toolbar-btn--secondary.ant-btn {
    background: #ffffff !important;
    border: 1px solid #d9dce3 !important;
    color: #4b5563 !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.toolbar-btn--secondary.ant-btn:hover {
    border-color: #c4b5fd !important;
    color: #1e3a5f !important;
    background: #f3f7fb !important;
}

.toolbar-btn--accent.ant-btn {
    background: linear-gradient(135deg, #e4eef6, #d5e6f3) !important;
    border: 1px solid rgba(42, 74, 122, 0.28) !important;
    color: #1e3a5f !important;
    box-shadow: 0 1px 2px rgba(109, 40, 217, 0.08);
}

.toolbar-btn--accent.ant-btn:hover {
    background: linear-gradient(135deg, #d5e6f3, #c5d9eb) !important;
    border-color: #2a4a7a !important;
    color: #4c1d95 !important;
}

.toolbar-btn--ghost-danger.ant-btn {
    background: transparent !important;
    border: 1px solid transparent !important;
    color: #dc2626 !important;
    box-shadow: none !important;
}

.toolbar-btn--ghost-danger.ant-btn:hover {
    background: #fef2f2 !important;
    border-color: #fecaca !important;
    color: #b91c1c !important;
}

/* Modal 內新增按鈕 */
.inline-add-btn.ant-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    color: #1e3a5f !important;
    background: linear-gradient(135deg, #f3f7fb, #e4eef6) !important;
    border: 1px solid rgba(42, 74, 122, 0.28) !important;
    border-radius: 9px !important;
    box-shadow: 0 1px 3px rgba(109, 40, 217, 0.1);
    transition: all 0.2s ease;
}

.inline-add-btn.ant-btn::before {
    content: '+';
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
    color: #2a4a7a;
}

.inline-add-btn.ant-btn:hover {
    color: #ffffff !important;
    background: #2a4a7a !important;
    border-color: transparent !important;
    box-shadow: 0 4px 12px rgba(109, 40, 217, 0.28);
}

.inline-add-btn.ant-btn:hover::before {
    color: #ffffff;
}

.editor-panel__label {
    font-size: 13px;
    font-weight: 600;
    color: #595959;
    margin-right: auto;
}

.editor-panel__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.code-textarea {
    font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.55;
    border-radius: 10px;
}

.code-textarea :deep(textarea) {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
}

.editor-panel__preview {
    border: 1px solid #e4e6ec;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    padding: 16px 20px;
}

.contract-preview-frame {
    display: block;
    width: 100%;
    height: 420px;
    border: none;
    background: #fff;
    box-sizing: border-box;
}

.field-spec-preview {
    padding: 18px 20px;
    min-height: 200px;
    max-height: 420px;
    overflow: auto;
    background: #f8f9fb;
    border: 1px solid #e4e6ec;
    border-radius: 10px;
}

.field-spec-preview--derived {
    background: linear-gradient(180deg, #f3f7fb 0%, #f8f9fb 48px);
    border-color: rgba(42, 74, 122, 0.2);
}

.field-spec-preview--error {
    background: #fff2f0;
    border-color: #ffccc7;
}

.field-spec-preview__error {
    margin: 0;
    color: #cf1322;
    font-size: 13px;
}

.field-spec-preview__note {
    margin: 0 0 16px;
    padding: 8px 12px;
    font-size: 12px;
    color: #1e3a5f;
    background: rgba(42, 74, 122, 0.08);
    border-radius: 8px;
}

.field-spec-preview__section {
    margin-bottom: 20px;
}

.field-spec-preview__section:last-child {
    margin-bottom: 0;
}

.field-spec-preview__heading {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: #434343;
}

.field-spec-preview__field {
    margin-bottom: 12px;
}

.field-spec-preview__field:last-child {
    margin-bottom: 0;
}

.field-spec-preview__field-label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #2a2a3a;
}

.field-spec-preview__required {
    color: #cf1322;
    margin-left: 2px;
}

.field-spec-preview__meta {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #8c8c8c;
}

.field-spec-preview__checkbox {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
    font-size: 13px;
    line-height: 1.55;
    color: #2a2a3a;
    cursor: default;
}

.field-spec-preview__checkbox:last-child {
    margin-bottom: 0;
}

.field-spec-preview__signature {
    margin-bottom: 12px;
}

.field-spec-preview__signature-box {
    margin-top: 6px;
    min-height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #bfbfbf;
    background: #fffef5;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
}

.field-spec-preview__hints {
    margin-top: 16px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #eceef3;
    border-radius: 8px;
}

.field-spec-preview__hints p {
    margin: 0;
    font-size: 12px;
    line-height: 1.55;
    color: #8c8c8c;
}

.field-spec-preview__hints p + p {
    margin-top: 4px;
}

.field-hint--spaced {
    margin-bottom: 20px;
}

.signing-section {
    margin-bottom: 28px;
}

.signing-section:last-child {
    margin-bottom: 0;
}

/* ── 欄位選擇器 ── */
.field-picker-section .items-editor__head--flat {
    border-top: none;
    padding-top: 0;
    margin-bottom: 8px;
}

.field-picker-hint {
    margin: 0 0 14px;
    font-size: 13px;
    color: #8c8c8c;
    line-height: 1.5;
}

.field-picker-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
    margin-bottom: 20px;
}

.field-picker-option {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 10px 14px;
    border: 1px solid #e4e6ec;
    border-radius: 10px;
    background: #fafbfc;
    cursor: pointer;
    transition:
        border-color 0.15s,
        background 0.15s,
        box-shadow 0.15s;
}

.field-picker-option:hover {
    border-color: #d3adf7;
    background: #fff;
}

.field-picker-option--on {
    border-color: #b37feb;
    background: #f9f0ff;
    box-shadow: 0 0 0 1px rgba(114, 46, 209, 0.12);
}

.field-picker-option :deep(.ant-checkbox-wrapper) {
    width: 100%;
    font-weight: 500;
    color: #2a2a3a;
}

.field-picker-empty {
    margin: 8px 0 4px;
}

.field-config-collapse {
    margin-top: 4px;
}

.signing-form-item {
    margin-bottom: 0;
}

.signing-agreement-card {
    margin-bottom: 14px;
}

.signing-agreement-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.signing-agreement-card__title {
    font-weight: 700;
    color: #722ed1;
}

@media (max-width: 720px) {
    .purchase-req-content {
        padding: 20px 16px !important;
    }

    .setting-row {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .settings-actions {
        justify-content: stretch;
    }

    .settings-actions .ant-btn {
        width: 100%;
    }

    .settings-radio-group :deep(.ant-radio-button-wrapper) {
        flex: 1 1 calc(50% - 4px);
        min-width: 0;
    }

    .field-picker-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.version-readonly__num {
    display: inline-block;
    font-size: 18px;
    font-weight: 700;
    color: #1f1f1f;
    line-height: 1.4;
}

.version-readonly__hint {
    margin: 6px 0 0;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.5;
}

.simple-doc {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.simple-doc__hint {
    margin: 0;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.6;
    color: #595959;
    background: #f7f8fa;
    border-radius: 8px;
}

.simple-doc__hint code {
    padding: 1px 6px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    font-size: 12px;
}

.token-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.token-bar__label {
    font-size: 12px;
    color: #8c8c8c;
    margin-right: 4px;
}

.token-chip {
    border-radius: 999px !important;
}

.simple-doc__form {
    margin-top: 4px;
}

.simple-doc__party-a {
    margin-top: 16px;
}

.advanced-json-collapse {
    margin-top: 16px;
    background: #fafafa;
    border-radius: 8px;
}

.advanced-json-collapse :deep(.ant-collapse-header) {
    font-size: 13px !important;
    color: #8c8c8c !important;
}

.preview-error {
    margin: 8px 0 0;
    color: #cf1322;
    font-size: 13px;
}
</style>
